import { FC, useEffect, useMemo } from 'react';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { useTranslation } from 'next-i18next';
import {
  ADULTS,
  DEPARTURE_DATE_FIELD,
  FORM_ID_BOOK_NOW_DIRECT,
  type BookNowDirectFormState,
} from 'src/book-now-direct/constants';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import { useUserContext } from 'src/common/providers/UserProvider';
import { Form } from 'react-final-form';
import FooterWhiteLabeled from 'src/payment/common/components/FooterWhiteLabeled';
import Typography from 'src/common/components/Typography';
import Button from 'src/common/components/ButtonDS22';
import Icon from 'src/common/components/IconDS22';
import FormStepDetails from 'src/book-now-direct/components/FormStepDetails';
import FormStepGuest from 'src/book-now-direct/components/FormStepGuest';
import { validateBookNowDirectForm } from 'src/book-now-direct/helpers';
import type { Occasion } from 'src/common/types/api/v4/trips/occasions';
import checkForContactInfo from 'src/common/utils/checkForContactInfo';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import CommunicationsWarningModal from 'src/admin/common/components/CommunicationsWarningModal';
import styles from './BookNowDirectRoot.module.scss';

export type Props = {
  listing: FullBoatSearchDetail;
  initialCapacity: number;
  /** Either from sessionStorage re-render, or from server via owner-provided url params */
  initialFormData?: BookNowDirectFormState;
  occasions: Occasion[];
};

const BookNowDirectRoot: FC<Props> = ({
  listing,
  initialCapacity,
  initialFormData,
  occasions,
}) => {
  const { t } = useTranslation('book-now-direct');
  const { open, isOpen, close } = useOpenCloseToggle(false);
  const { user } = useUserContext();
  const { submitForm, formStep, fetchAndUpdateInstabooksOnDate } = useBookNowDirectContext();

  const isDetailsStep = formStep === 'details';
  const isGuestUser = !user?.id;

  const initialFormValues = useMemo(() => ({
    [ADULTS]: initialCapacity,
    ...initialFormData,
  }), [initialCapacity, initialFormData]);

  useEffect(() => {
    if (initialFormData?.[DEPARTURE_DATE_FIELD]) {
      fetchAndUpdateInstabooksOnDate(
        initialFormData[DEPARTURE_DATE_FIELD],
        initialFormData[ADULTS] || initialCapacity,
      );
    }
  }, [fetchAndUpdateInstabooksOnDate, initialCapacity, initialFormData]);

  const onSubmit = async (values: BookNowDirectFormState) => {
    const { messageToOwner } = values;

    if (messageToOwner && isDetailsStep) {
      const hasContactInfo = await checkForContactInfo([messageToOwner]);
      if (hasContactInfo && !isOpen) {
        return open();
      }
      if (isGuestUser && isOpen) {
        close();
      }
    }

    return submitForm(values);
  };

  return (
    <Form<BookNowDirectFormState>
      initialValues={initialFormValues}
      validate={validateBookNowDirectForm(formStep, t)}
      onSubmit={onSubmit}
    >
      {({
        submitFailed,
        modifiedSinceLastSubmit,
        handleSubmit,
        submitError,
        submitting,
      }) => (
        <form
          method="POST"
          id={FORM_ID_BOOK_NOW_DIRECT}
          className={styles.root}
          onSubmit={handleSubmit}
        >
          <div className={styles.content}>
            <FormStepDetails listing={listing} occasions={occasions} />
            <FormStepGuest />
            <div className={styles.poweredBy}>
              <FooterWhiteLabeled />
            </div>
          </div>
          <div className={styles.footer}>
            {submitFailed && !modifiedSinceLastSubmit && (
              <span className={styles.errors}>
                <Icon
                  id="warning"
                  size="m"
                />
                <Typography
                  type="p"
                  size="body-s"
                >
                  {submitError ?? t('submit-error')}
                </Typography>
              </span>
            )}
            <Button
              formId={FORM_ID_BOOK_NOW_DIRECT}
              type="submit"
              variant="instabook-green"
              loading={submitting && !isOpen}
            >
              {t('cta-continue')}
            </Button>
          </div>
          <CommunicationsWarningModal
            isOpen={isOpen}
            handleSubmit={handleSubmit}
            submitting={submitting}
          />
        </form>
      )}
    </Form>
  );
};

export default BookNowDirectRoot;
